import axios from "axios";
import {
  saveToLocalDB,
  loadFromLocalDB,
  syncLipidsData,
  deleteFromLocalDB,
} from "./dataService";

const api = axios.create({
  /*   baseURL: "/api", // Используйте прокси */
  baseURL: "https://lipapi0.grace3.ru",
  timeout: 15000, // Увеличиваем тайм-аут до 15 секунд
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  withCredentials: true, // Исключаем передачу credentials (cookies/HTTP auth)
});

// API endpoints
export const API_ENDPOINTS = {
  SURVEY: "ind_risk/my_risk/add_info_with_cookie",
  LOGOUT: "auth/logout",
  LATEST_RISK: "ind_risk/my_risk/latest_info_cookie",
} as const;

// Глобальный токен Authorization
let authToken: string | null = null;
// Инициализация токена из куки
const initializeAuthTokenFromCookie = () => {
  const cookieToken = document.cookie
    .split("; ")
    .find((row) => row.startsWith("authToken="))
    ?.split("=")[1];

  if (cookieToken) {
    authToken = cookieToken;
    console.log("Токен загружен из куки:", authToken);
  }
};

// Вызываем функцию для инициализации токена из куки
initializeAuthTokenFromCookie();
// Функция для установки токена
export const setAuthorizationToken = (token: string) => {
  authToken = token;
  localStorage.setItem("authToken", token); // Сохраняем токен в localStorage
  console.log("Токен установлен:", authToken);
};

api.interceptors.request.use(
  (config) => {
    // Не добавляем токен для эндпоинтов регистрации
    if (
      config.url?.includes("/users/user_register/") ||
      config.url?.includes("/users/user_register_cookie/")
    ) {
      return config;
    }

    // Сначала проверяем токен в localStorage
    const token = localStorage.getItem("authToken");
    // Если нет в localStorage, пробуем получить из cookie
    if (!token) {
      const cookieToken = document.cookie
        .split("; ")
        .find((row) => row.startsWith("authToken="))
        ?.split("=")[1];

      if (cookieToken) {
        config.headers.Authorization = `Bearer ${cookieToken}`;
      }
    } else {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Добавляем интерцептор ответа для обработки ошибок аутентификации
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Обработка ошибок аутентификации
    if (error.response?.status === 401) {
      document.cookie =
        "authToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      localStorage.removeItem("authToken");
    }

    // Обработка сетевых ошибок
    if (
      error.code === "ERR_NETWORK" ||
      error.code === "ERR_INTERNET_DISCONNECTED"
    ) {
      console.error("Сетевая ошибка:", error.message);
      error.message =
        "Ошибка подключения к сети. Проверьте интернет-соединение.";
    } else if (
      error.code === "ECONNABORTED" ||
      error.message?.includes("timeout")
    ) {
      console.error("Таймаут:", error.message);
      error.message = "Превышено время ожидания ответа сервера.";
    }

    return Promise.reject(error);
  }
);

export default api;

/**
 * Кодирует строку в Base64
 */
const encodeBase64 = (str: string) => {
  return btoa(unescape(encodeURIComponent(str)));
};

/**
 * Авторизация пользователя
 */
export const signIn = async (uid: string, pass: string) => {
  try {
    console.log("signIn вызван с параметрами:", { uid, pass });
    const cleanedUid = uid.replace(/-/g, "");
    const formattedPass = pass.split(".").reverse().join("-");
    console.log("Обработанные параметры:", { cleanedUid, formattedPass });

    const requestBody = {
      uid: cleanedUid,
      password: formattedPass,
    };

    console.log("Отправляемые данные в /auth/login_cookie:", requestBody);

    const response = await api.post("/auth/login_cookie", requestBody);

    if (!response.data.access_token) {
      throw new Error("Токен не получен от сервера");
    }

    // Сохраняем токен в куки
    document.cookie = `authToken=${response.data.access_token}; path=/; secure; samesite=strict`;

    // Устанавливаем токен для будущих запросов
    setAuthorizationToken(response.data.access_token);

    // Загружаем данные профиля после успешной авторизации
    await fetchUserProfile();

    // Синхронизация данных после успешной авторизации
    await syncLipidsData();

    return {
      message: "Авторизация успешна",
      userId: uid,
    };
  } catch (error: any) {
    if (error.response) {
      if (error.response.status === 401) {
        throw new Error("Ошибка: неверный UID или дата рождения.");
      }
      // Добавляем обработку других статусов ошибок
      if (error.response.status === 400) {
        throw new Error("Ошибка: неверный формат данных.");
      }
      if (error.response.status === 403) {
        throw new Error("Ошибка: доступ запрещен.");
      }
    }
    console.error("Ошибка при авторизации:", error);
    throw new Error("Ошибка авторизации. Повторите попытку позже.");
  }
};

export const addLipids = async (lipids: {
  total_cholesterol: number;
  triglycerides: number;
  ldl: number;
  hdl: number;
  lpa: 0;
  ai_conclusion: "null";
}) => {
  try {
    const response = await api.post(
      "/lipids/my_lipids/add_lipids_cookie",
      lipids
    );

    // Обновляем локальную базу через синхронизацию
    await syncLipidsData();

    // Сохраняем данные липидного профиля в localStorage для расчета рисков
    const lipidData = {
      total_cholesterol: lipids.total_cholesterol,
      hdl: lipids.hdl,
    };
    localStorage.setItem("lipid_data", JSON.stringify(lipidData));

    return response.data;
  } catch (error: any) {
    if (error.response && error.response.status === 422) {
      throw new Error("Ошибка валидации данных. Проверьте введенные значения.");
    }
    console.error("Ошибка при добавлении липидограммы:", error);
    throw new Error("Не удалось добавить анализ липидограммы.");
  }
};

export const fetchLipids = async () => {
  try {
    // Проверяем наличие данных в локальной базе данных
    const localData = await loadFromLocalDB("lipids");

    if (localData && localData.length > 0) {
      console.log("Данные загружены из локальной БД.");
      return localData;
    }

    // Если локальные данные отсутствуют, делаем запрос к API
    const response = await api.get("/lipids/my_lipids/info_cookie");

    if (response.data) {
      console.log("Данные загружены с API и сохранены в локальную БД.");
      await saveToLocalDB("lipids", response.data);
      return response.data;
    }

    throw new Error("Нет данных липидограммы.");
  } catch (error: any) {
    if (error.response?.status === 404) {
      console.log("У пользователя нет данных липидограмм");
      return null;
    }
    console.error("Ошибка при получении данных липидограммы:", error);
    throw new Error("Не удалось получить данные липидограммы.");
  }
};

export const fetchLipids_onlyapi = async () => {
  try {
    // Очищаем локальные данные перед запросом
    await deleteFromLocalDB("lipids");

    // Делаем запрос к API
    const response = await api.get("/lipids/my_lipids/info_cookie");

    if (response.data) {
      await saveToLocalDB("lipids", response.data);
      return response.data;
    }
    return null;
  } catch (error: any) {
    if (error.response?.status === 404) {
      console.log("У пользователя нет данных липидограмм");
      return null;
    }
    console.error("Ошибка при получении данных липидограммы:", error);
    throw new Error("Не удалось получить данные липидограммы.");
  }
};

export const fetchLatestLipidsData_stable = async () => {
  try {
    // Проверяем наличие данных в локальной базе данных
    const localData = await loadFromLocalDB("lipids");

    if (localData && localData.length > 0) {
      console.log("Последние данные липидограммы загружены из локальной БД.");
      return localData[localData.length - 1]; // Возвращаем последнюю запись
    } else {
      console.log("Локальные данные отсутствуют, синхронизация с API.");
      // Если локальные данные отсутствуют, запускаем синхронизацию
      const syncedData = await syncLipidsData();
      return syncedData[syncedData.length - 1]; // Возвращаем последнюю запись
    }
  } catch (error: any) {
    console.error("Ошибка при получении последних данных липидограммы:", error);
    throw new Error("Не удалось получить последние данные липидограммы.");
  }
};

export const fetchLatestLipidsData = async () => {
  try {
    // Проверяем наличие данных в локальной базе данных
    const localData = await loadFromLocalDB("lipids");

    if (localData && localData.length > 1) {
      console.log("Последние данные липидограммы загружены из локальной БД.");
      const lastRecord = localData[localData.length - 1];
      const secondLastRecord = localData[localData.length - 2];

      const dynamics = {
        total_cholesterol: lastRecord.total_cholesterol,
        dyn_total_cholesterol: compareValues(
          lastRecord.total_cholesterol,
          secondLastRecord.total_cholesterol
        ),
        tg: lastRecord.triglycerides,
        dyn_tg: compareValues(
          lastRecord.triglycerides,
          secondLastRecord.triglycerides
        ),
        hdl: lastRecord.hdl,
        dyn_hdl: compareValues(lastRecord.hdl, secondLastRecord.hdl),
        ldl: lastRecord.ldl,
        dyn_ldl: compareValues(lastRecord.ldl, secondLastRecord.ldl),
        date: lastRecord.created_at,
      };

      return dynamics;
    } else if (localData && localData.length === 1) {
      console.log("Только одна запись в локальной БД.");
      const lastRecord = localData[0];
      return {
        total_cholesterol: lastRecord.total_cholesterol,
        dyn_total_cholesterol: 0,
        tg: lastRecord.tg,
        dyn_tg: 0,
        hdl: lastRecord.hdl,
        dyn_hdl: 0,
        ldl: lastRecord.ldl,
        dyn_ldl: 0,
        date: lastRecord.created_at,
      };
    } else {
      console.log("Локальные данные отсутствуют, синхронизация с API.");
      const syncedData = await syncLipidsData();
      if (syncedData.length > 1) {
        const lastRecord = syncedData[syncedData.length - 1];
        const secondLastRecord = syncedData[syncedData.length - 2];

        const dynamics = {
          total_cholesterol: lastRecord.total_cholesterol,
          dyn_total_cholesterol: compareValues(
            lastRecord.total_cholesterol,
            secondLastRecord.total_cholesterol
          ),
          tg: lastRecord.tg,
          dyn_tg: compareValues(lastRecord.tg, secondLastRecord.tg),
          hdl: lastRecord.hdl,
          dyn_hdl: compareValues(lastRecord.hdl, secondLastRecord.hdl),
          ldl: lastRecord.ldl,
          dyn_ldl: compareValues(lastRecord.ldl, secondLastRecord.ldl),
          date: lastRecord.created_at,
        };

        return dynamics;
      } else if (syncedData.length === 1) {
        const lastRecord = syncedData[0];
        return {
          total_cholesterol: lastRecord.total_cholesterol,
          dyn_total_cholesterol: 0,
          tg: lastRecord.tg,
          dyn_tg: 0,
          hdl: lastRecord.hdl,
          dyn_hdl: 0,
          ldl: lastRecord.ldl,
          dyn_ldl: 0,
          date: lastRecord.created_at,
        };
      } else {
        console.warn("Нет данных липидограммы.");
        return null;
      }
    }
  } catch (error: any) {
    console.error("Ошибка при получении последних данных липидограммы:", error);
    return null;
  }
};

/**
 * Сравнивает два значения и возвращает 0, если они равны, 1, если первое меньше, и 2, если первое больше.
 */
const compareValues = (current: number, previous: number): number => {
  if (current === previous) return 0;
  return current < previous ? 1 : 2;
};

export const registerUser = async (userData: {
  last_name: string;
  first_name: string;
  middle_name: string;
  birth_date: string;
  sex: string;
  phone_number: string;
  email: string;
}) => {
  try {
    console.log("Отправляем данные регистрации:", userData);
    const response = await api.post("/users/user_register_cookie/", userData, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      timeout: 15000,
      withCredentials: false, // Отключаем credentials для регистрации
    });

    if (response.data) {
      console.log("Данные профиля загружены с API и сохранены в локальную БД.");
      await saveToLocalDB("profile", response.data);
      return response.data;
    }
  } catch (error: any) {
    console.log("Данные для регистрации:", userData);
    console.error("Ошибка при регистрации пользователя:", error);

    // Обработка различных типов ошибок
    if (
      error.code === "ERR_NETWORK" ||
      error.code === "ERR_INTERNET_DISCONNECTED"
    ) {
      throw new Error(
        "Ошибка подключения к сети. Проверьте интернет-соединение и попробуйте снова."
      );
    } else if (
      error.code === "ECONNABORTED" ||
      error.message?.includes("timeout")
    ) {
      throw new Error("Время ожидания истекло. Попробуйте снова.");
    } else if (error.response) {
      // Ошибка от сервера
      const status = error.response.status;
      const message =
        error.response.data?.message ||
        error.response.data?.detail ||
        "Неизвестная ошибка сервера";

      console.log("Детали ошибки сервера:", {
        status,
        data: error.response.data,
        headers: error.response.headers,
      });

      if (status === 400) {
        throw new Error(`Ошибка в данных: ${message}`);
      } else if (status === 422) {
        // Более детальная обработка ошибок валидации
        let validationMessage = "Ошибка валидации данных";
        if (error.response.data?.detail) {
          if (Array.isArray(error.response.data.detail)) {
            validationMessage = error.response.data.detail
              .map((err: any) => `${err.loc?.join(".") || "Поле"}: ${err.msg}`)
              .join("; ");
          } else {
            validationMessage = error.response.data.detail;
          }
        } else if (error.response.data?.message) {
          validationMessage = error.response.data.message;
        }
        throw new Error(`Ошибка валидации: ${validationMessage}`);
      } else if (status === 500) {
        throw new Error("Внутренняя ошибка сервера. Попробуйте позже.");
      } else {
        throw new Error(`Ошибка сервера (${status}): ${message}`);
      }
    } else {
      throw new Error("Произошла неожиданная ошибка. Попробуйте снова.");
    }
  }
};

// Start of Selection
export const sendSurveyData = async (surveyData: {
  height: number;
  weight: number;
  smoke: boolean;
  sb_pressure: number;
  db_pressure: number;
  question_0: boolean;
  question_1: boolean;
  question_2: boolean;
  question_3: boolean;
  question_4: boolean;
  question_5: boolean;
  question_6: boolean;
  question_7: boolean;
  question_8: boolean;
  question_9: boolean;
  result_risk: string;
  heart_risk: string;
  hypertension_risk: string;
  lipid_risk: string;
  genetic_risk: string;
  smoke_risk: string;
}) => {
  try {
    console.log("URL запроса:", API_ENDPOINTS.SURVEY);
    console.log("Отправляемые данные в API:", surveyData);
    const response = await api.post(API_ENDPOINTS.SURVEY, surveyData);
    console.log("Ответ сервера в API:", response.data);

    // После успешной отправки данных, получаем обновленные данные с сервера
    try {
      const updatedRiskData = await fetchLatestRiskInfo();
      console.log("Данные рисков обновлены в локальной БД:", updatedRiskData);
    } catch (updateError) {
      console.error("Ошибка при обновлении данных рисков:", updateError);
      // Не прерываем выполнение, так как основные данные уже сохранены
    }

    return response.data;
  } catch (error: any) {
    console.error("Ошибка при отправке данных:", error.response || error);
    if (error.response) {
      throw new Error(
        `Ошибка сервера: ${error.response.status} - ${
          error.response.data?.message || "Неизвестная ошибка"
        }`
      );
    }
    throw error;
  }
};

export const calculateAge = (
  birthDateString: string,
  currentDate: Date
): number => {
  const birthDate = new Date(birthDateString);
  let age = currentDate.getFullYear() - birthDate.getFullYear();
  const monthDifference = currentDate.getMonth() - birthDate.getMonth();
  if (
    monthDifference < 0 ||
    (monthDifference === 0 && currentDate.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age;
};

export const fetchUserProfile = async () => {
  try {
    const response = await api.get("/users/get_user_profile_cookie");

    if (response.data) {
      // Сохраняем данные профиля в локальную БД
      await saveToLocalDB("profile", response.data);

      // Сохраняем необходимые для расчета риска данные в localStorage
      const userData = {
        gender:
          response.data.sex === "Мужской" || response.data.sex === "male"
            ? "male"
            : "female",
        birth_date: response.data.birth_date,
      };
      localStorage.setItem("user_data", JSON.stringify(userData));

      console.log("Данные профиля получены и сохранены:", response.data);
      return response.data;
    }
  } catch (error: any) {
    console.error("Ошибка при получении данных профиля:", error);
    throw new Error("Не удалось получить данные профиля");
  }
};

// Получение данных о весе
export const fetchWeight = async () => {
  try {
    const response = await api.get("/body_weight/my_weight/info_cookie");

    if (response.data) {
      // Сохраняем данные в локальную БД
      await saveToLocalDB("weight", response.data);
      console.log("Данные веса получены и сохранены:", response.data);
      return response.data;
    }
  } catch (error: any) {
    if (error.response?.status === 404) {
      console.log("У пользователя нет данных о весе");
      return null;
    }
    console.error("Ошибка при получении данных веса:", error);
    throw new Error("Не удалось получить данные о весе");
  }
};

// Добавление новой записи веса
export const addWeight = async (weightData: {
  current_weight: number;
  bmi: number;
}) => {
  try {
    const response = await api.post(
      "/body_weight/my_weight/add_weight_cookie",
      weightData
    );

    if (response.data) {
      // После успешного добавления обновляем локальные данные
      await fetchWeight();
      return response.data;
    }
  } catch (error: any) {
    if (error.response?.status === 422) {
      throw new Error("Ошибка валидации данных. Проверьте введенные значения.");
    }
    console.error("Ошибка при добавлении записи веса:", error);
    throw new Error("Не удалось добавить запись о весе");
  }
};

// Получение данных веса только с API (для обновления)
export const fetchWeight_onlyapi = async () => {
  try {
    // Очищаем локальные данные перед запросом
    await deleteFromLocalDB("weight");

    // Делаем запрос к API
    const response = await api.get("/body_weight/my_weight/info_cookie");

    if (response.data) {
      await saveToLocalDB("weight", response.data);
      return response.data;
    }
    return null;
  } catch (error: any) {
    if (error.response?.status === 404) {
      console.log("У пользователя нет данных о весе");
      return null;
    }
    console.error("Ошибка при получении данных веса:", error);
    throw new Error("Не удалось получить данные о весе");
  }
};

export const logout = async () => {
  try {
    const response = await api.post(API_ENDPOINTS.LOGOUT);
    return response.data;
  } catch (error: any) {
    console.error("Ошибка при выходе из системы на сервере:", error);

    // Обработка различных типов ошибок
    if (
      error.code === "ERR_NETWORK" ||
      error.code === "ERR_INTERNET_DISCONNECTED"
    ) {
      throw new Error(
        "Ошибка подключения к сети при выходе. Проверьте интернет-соединение и попробуйте снова."
      );
    } else if (
      error.code === "ECONNABORTED" ||
      error.message?.includes("timeout")
    ) {
      throw new Error("Время ожидания выхода истекло. Попробуйте снова.");
    } else if (error.response) {
      // Ошибка от сервера
      const status = error.response.status;
      const message =
        error.response.data?.message ||
        error.response.data?.detail ||
        "Неизвестная ошибка сервера при выходе";

      console.log("Детали ошибки сервера при выходе:", {
        status,
        data: error.response.data,
        headers: error.response.headers,
      });

      if (status === 400) {
        throw new Error(`Ошибка в данных при выходе: ${message}`);
      } else if (status === 401) {
        throw new Error(
          "Неавторизованный запрос при выходе. Возможно, сессия истекла."
        );
      } else if (status === 500) {
        throw new Error(
          "Внутренняя ошибка сервера при выходе. Попробуйте позже."
        );
      } else {
        throw new Error(`Ошибка сервера при выходе (${status}): ${message}`);
      }
    } else {
      throw new Error(
        "Произошла неожиданная ошибка при выходе. Попробуйте снова."
      );
    }
  }
};

export const fetchLatestRiskInfo = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.LATEST_RISK);
    if (response.data) {
      // Сохраняем данные в локальную БД
      await saveToLocalDB("latest_risk", response.data);
      return response.data;
    }
    return null;
  } catch (error: any) {
    if (error.response?.status === 404) {
      console.log("У пользователя нет данных о рисках");
      return null;
    }
    console.error("Ошибка при получении данных о рисках:", error);
    throw new Error("Не удалось получить данные о рисках");
  }
};
